import { useState, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { t } from "i18next";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import CustomModal from "components/Modals/CustomModal";
import AICCRuleBuilder from "./aiccRuleBuilder/AICCRuleBuilder";
import { useRuleDataContext } from "../../context/RuleDataProvider";
import rulesApi from "services/apis/rulesApi";
import RuleBuilderConvert from "utils/converter/RuleBuilderConvert";
import CenterLoading from "components/common/CenterLoading";

const RuleCreationTab = ({ viewOnly, onRuleReset }) => {
  const fileInputRef = useRef(null);
  const { query, setQuery, groupData, ruleQueryString } = useRuleDataContext();
  const [showQuery, setShowQuery] = useState(false);
  const [confirmQueryReset, setConfirmQueryReset] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [configList] = useState([]);
  const [dataToProcess, setDataToProcess] = useState({});
  const [evaluationResult, setEvaluationResult] = useState({});
  const companyDetails = useSelector((state) => state?.company);
  const isTestRuleUnderConstruction =
    companyDetails?.subscriptions?.testRuleUnderConstruction;

  const fieldsWithGroup = useMemo(() => {
    return RuleBuilderConvert.aiccRBFieldConfigToGroupedFields(groupData, [
      ...configList.flatMap((e) => e.aiccDataStreamJsons),
      ...configList
        .flatMap((e) => e.apiDetails ?? [])
        .map((e) => ({
          ...e,
          jsonId: e.dataStreamConfigApiId,
          jsonFileSchemaName: e.apiName,
        })),
    ]);
  }, [configList, groupData]);
  const evaluateRuleExpressions = async () => {
    setIsEvaluating(true);
    try {
      const whereClauses = RuleBuilderConvert.aiccRBQueryToWhereClause(
        query,
        true
      )?.map((whereClause) => {
        const ids = [whereClause.leftOperand];
        const leftOpArithmetic = (whereClause.leftOpArithmetic ?? "").split(
          " "
        );
        if (leftOpArithmetic && whereClause.isLeftFieldType) {
          ids.push(Number(leftOpArithmetic[1]));
        }
        if (whereClause.isFieldType) {
          ids.push(Number(whereClause.rightOperand));
        }
        const expressionValues =
          groupData
            .filter((groupItem) => {
              return ids.includes(groupItem.customFieldsId);
            })
            ?.map((groupItem) => {
              return {
                groupId: groupItem.groupId,
                customFieldId: groupItem.customFieldsId,
                queryString: groupItem.queryString,
                expressionIsParentChildRelationship:
                  groupItem.isParentChildRelationship,
                queryValue: "",
              };
            }) ?? [];
        return {
          ...whereClause,
          expressionValues,
        };
      });

      const response = await rulesApi.evaluateRuleExpressions({
        jsonPayload: dataToProcess,
        whereClauses,
      });
      const result =
        response?.data?.reduce((acc, data) => {
          return {
            ...acc,
            [data.expressionKey]: {
              result: data.expressionResult,
              values: data.expressionValues,
            },
          };
        }, {}) ?? {};
      setEvaluationResult(result);
      setIsEvaluating(false);
    } catch (error) {
      setIsEvaluating(false);
      console.log(error);
    }
  };

  const queryChangeCallback = useCallback(
    (myQuery) => {
      setQuery(myQuery);
    },
    [setQuery]
  );
  return (
    <div>
      <div className="rule-creation-header">
        <div className="rule-info">
          <span className="info-icon">
            <i className="fa fa-info-circle"></i>
          </span>
          <div className="info-text">
            <span>
              <strong>{t("aiccRuleBuilder.andCombinatorLabel")}</strong> –{" "}
              {t("aiccRuleBuilder.andCombinatorInfo")}
            </span>
            <span>
              <strong>{t("aiccRuleBuilder.orCombinatorLabel")}</strong> –{" "}
              {t("aiccRuleBuilder.orCombinatorInfo")}
            </span>
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-between w-100">
          <div className="d-flex gap-2">
            {isTestRuleUnderConstruction && (
              <>
                <PrimaryButton
                  icon="fa-regular fa-folder-open"
                  onClick={() => {
                    fileInputRef.current.click();
                  }}
                  disabled={viewOnly}
                  variant="secondary"
                />
                <input
                  type="file"
                  accept=".json,application/json"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={async (event) => {
                    const file = event.target.files[0];
                    if (!file) return;

                    const text = await file.text();
                    try {
                      const data = JSON.parse(text);
                      setDataToProcess(data);
                    } catch (err) {
                      alert("Invalid JSON format");
                    }
                  }}
                />
                <PrimaryButton
                  title={t("Evaluate")}
                  icon="fa-solid fa-gears"
                  onClick={evaluateRuleExpressions}
                  disabled={!Object.keys(dataToProcess).length}
                  variant="secondary"
                />
                {Object.keys(evaluationResult).length > 0 && (
                  <PrimaryButton
                    title={t("Clear")}
                    onClick={() => {
                      setEvaluationResult({});
                    }}
                    disabled={!Object.keys(dataToProcess).length}
                    variant="tertiary"
                  />
                )}
              </>
            )}
          </div>
          <div className="d-flex gap-2">
            {ruleQueryString && (
              <PrimaryButton
                title={t("createRuleEngine.ruleCreationTab.viewQueryButton")}
                icon="fa-regular fa-eye"
                onClick={() => {
                  setShowQuery(true);
                }}
                variant="secondary"
              />
            )}

            <PrimaryButton
              title={t("CommonKeys.reset")}
              icon="fa-solid fa-arrows-rotate"
              onClick={() => {
                setConfirmQueryReset(true);
              }}
              disabled={
                viewOnly ||
                (query ?? []).filter((e) => !e.isDeleted).length <= 0
              }
              variant="secondary"
            />
          </div>
        </div>
      </div>

      <CustomModal
        size={"xl"}
        show={showQuery}
        handleClose={() => setShowQuery(false)}
        title={t("createRuleEngine.breadCrumb.title")}
        body={ruleQueryString}
        centered
        okTitle={t("CommonKeys.okButtonTitle")}
        onOk={() => setShowQuery(false)}
      />

      <CustomModal
        show={confirmQueryReset}
        handleClose={() => setConfirmQueryReset(false)}
        title={t("createRuleEngine.breadCrumb.title")}
        body={
          <span className="text-danger fw-bold">
            {t("createRuleEngine.resetRuleBuilder")}
          </span>
        }
        centered
        okTitle={t("CommonKeys.yes")}
        onOk={() => {
          const temp = Array.from(query);

          const finalQuery = temp
            .filter((e) => e.expressionId)
            .map((e) => ({ ...e, isDeleted: true }));

          setQuery(finalQuery);
          setConfirmQueryReset(false);
          onRuleReset();
        }}
        cancelTitle={t("CommonKeys.no")}
        onCancel={() => {
          setConfirmQueryReset(false);
        }}
      />
      <CenterLoading asModal={true} isVisible={isEvaluating} />
      <AICCRuleBuilder
        query={query}
        onChangeQuery={queryChangeCallback}
        useFieldGroup
        allowValueSource
        allowArithmetic
        fieldsWithGroup={fieldsWithGroup}
        evaluationResult={evaluationResult}
      />
    </div>
  );
};

export default RuleCreationTab;
